// The unmatched-bookings inbox (UC-APPOINTMENTS-004; BR-APPOINTMENTS-005, -006; D37): each booking Vision X could not place,
// why (no match, several possible patients, an archived patient) and the candidates, with Link (a candidate or a searched
// patient), Create patient (pre-filled from the booking: name split at the last space, email, phone) and Ignore with a reason.
import { CreatePatientInputSchema, parseSaId, type BookingsInboxItem, type DuplicateMatch, type PatientSearchResult } from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent, type ReactNode } from "react";
import { newIdempotencyKey } from "../api/client";
import { bookings, patientLookup } from "../api/endpoints.appointments";
import { describeError, isApiError } from "../api/problem";
import { formatDate, formatDateTime } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Chip, Empty, ErrorAlert, Field, Loading, Modal, Panel, Select, TextArea, TextInput } from "../ui/bits";
import { matchedOnText } from "./PatientsShared";

const REASON_WORD: Record<BookingsInboxItem["reason"], string> = { NoMatch: "No match", Ambiguous: "Several possible", PatientArchived: "Archived patient" };

export function InboxPanel({ branchId, highlight }: { branchId: string | undefined; highlight: boolean }) {
  const s = useSessionView();
  const qc = useQueryClient();
  // No branch, no inbox: the API refuses the list without one, and the page already says there is no active branch (review, 4 Oct 2026).
  const list = useQuery({ queryKey: ["inbox", branchId ?? ""], queryFn: () => bookings.inbox({ status: "Unmatched", branchId }), enabled: !!branchId, refetchInterval: 60_000 });
  const [modal, setModal] = useState<{ kind: "link" | "create" | "ignore"; item: BookingsInboxItem } | null>(null);
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const items = list.data?.data.items ?? [];
  const canWrite = can(s, "appointments.appointment.write");
  // Offline or suspended, the resolutions are disabled with the reason, as every other write is (D13).
  const block = useWriteBlock();
  const blockTitle = block ? WRITE_BLOCK_TITLE[block] : undefined;
  const resolved = (what: string) => {
    setModal(null);
    setNotice(<Alert tone="ok"><b>{what}</b> The appointment is on the calendar.</Alert>);
    void qc.invalidateQueries({ queryKey: ["inbox"] });
    void qc.invalidateQueries({ queryKey: ["calendar"] });
    void qc.invalidateQueries({ queryKey: ["today"] });
  };
  if (!branchId) return null;
  return (
    <Panel id="inbox" className={highlight ? "hl" : undefined} title="Unmatched bookings" meta={list.data ? `${items.length} to match` : "from Zoho Bookings"} tight>
      {notice && <div className="panel-body">{notice}</div>}
      {list.isPending && <Loading what="unmatched bookings" />}
      {list.isError && <div className="panel-body"><ErrorAlert error={list.error} /></div>}
      {list.data && items.length === 0 && <Empty title="Every booking is matched.">A booking whose name, email and mobile match no patient waits here until you link it.</Empty>}
      {items.length > 0 && (
        <div className="list">
          {items.map((it) => (
            <div key={it.id} className="item inbox-item">
              <div className="grow">
                <div className="primary">{it.customerName ?? "Unnamed customer"} <Chip tone={it.reason === "PatientArchived" ? "crit" : it.reason === "Ambiguous" ? "warn" : "neutral"}>{it.reason === "Ambiguous" ? `${it.candidates.length} possible` : REASON_WORD[it.reason]}</Chip></div>
                <div className="sub">{formatDateTime(it.startAt)} · {it.serviceName ?? "service unknown"} · {it.staffName ?? "unassigned"}{it.customerMobile ? ` · ${it.customerMobile}` : ""}{it.customerEmail ? ` · ${it.customerEmail}` : ""}</div>
              </div>
              {canWrite && (
                <div className="row c gap-4">
                  <button className="btn sm" type="button" disabled={block !== null} title={blockTitle} onClick={() => setModal({ kind: "link", item: it })}>Link</button>
                  {can(s, "patients.patient.write") && it.reason !== "PatientArchived" && <button className="btn sm" type="button" disabled={block !== null} title={blockTitle} onClick={() => setModal({ kind: "create", item: it })}>Create</button>}
                  <button className="btn sm ghost" type="button" disabled={block !== null} title={blockTitle} onClick={() => setModal({ kind: "ignore", item: it })}>Ignore</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {modal?.kind === "link" && <LinkModal item={modal.item} onClose={() => setModal(null)} onDone={() => resolved("Linked.")} />}
      {modal?.kind === "create" && <CreateModal item={modal.item} onClose={() => setModal(null)} onDone={resolved} />}
      {modal?.kind === "ignore" && <IgnoreModal item={modal.item} onClose={() => setModal(null)} onDone={() => { setModal(null); setNotice(<Alert tone="ok"><b>Ignored.</b> Later pulls leave this booking alone.</Alert>); void qc.invalidateQueries({ queryKey: ["inbox"] }); }} />}
    </Panel>
  );
}

function LinkModal({ item, onClose, onDone }: { item: BookingsInboxItem; onClose: () => void; onDone: () => void }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const search = useQuery({ queryKey: ["patient-search", q], queryFn: () => patientLookup.search(q), enabled: q.trim().length >= 2 });
  const link = async (patientId: string) => {
    setBusy(true);
    setError(null);
    try { await bookings.link(item.id, patientId); onDone(); }
    catch (err) { setError(err); }
    finally { setBusy(false); }
  };
  const row = (p: PatientSearchResult | BookingsInboxItem["candidates"][number], matched: string[]) => (
    <div key={p.id} className="item">
      <div className="grow"><div className="primary">{p.displayName} <span className="mono faint">{p.accountCode}</span></div><div className="sub">{p.status}{matched.length > 0 ? ` · matched on ${matchedOnText(matched)}` : ""}</div></div>
      <button className="btn sm" type="button" disabled={busy || p.status === "Archived"} onClick={() => void link(p.id)}>Link</button>
    </div>
  );
  return (
    <Modal title={`Link ${item.customerName ?? "booking"}`} meta={`${formatDateTime(item.startAt)} · ${item.serviceName ?? ""}`} onClose={onClose} footer={<button className="btn" type="button" onClick={onClose}>Cancel</button>}>
      {error !== null && <Alert tone="crit">{isApiError(error) && error.code === "PATIENT_NOT_TRANSACTABLE" ? "That patient is archived; un-archive the record first." : describeError(error)}</Alert>}
      {item.candidates.length > 0 && (
        <div className="stack gap-4"><div className="strong">Possible matches</div><div className="list bordered">{item.candidates.map((c) => row(c, c.matchedOn))}</div></div>
      )}
      <Field label="Or find another patient" htmlFor="inbox-search" help="Name, account code, mobile or email — two characters to search.">
        <TextInput id="inbox-search" value={q} autoFocus onChange={(e) => setQ(e.target.value)} placeholder="Search patients" />
      </Field>
      {search.isError && <ErrorAlert error={search.error} />}
      {search.data && (search.data.data.items.length === 0 ? <span className="help">No patient matches.</span> : <div className="list bordered">{search.data.data.items.map((p) => row(p, []))}</div>)}
    </Modal>
  );
}

/** The fields the create form shows; an error on any other path is shown in the alert. */
const CREATE_FIELDS = new Set(["firstNames", "surname", "idNumber", "dateOfBirth", "gender", "contact.mobile", "contact.email"]);
/** The patients module's duplicate refusal (D42, BR-10): an exact ID match, or a probable match to acknowledge, with the matches in `meta.duplicates`. */
function duplicateRefusal(err: unknown): { kind: "exact" | "probable"; matches: DuplicateMatch[] } | null {
  if (!isApiError(err) || err.code !== "VALIDATION_FAILED" || !Array.isArray(err.meta.duplicates)) return null;
  const matches = err.meta.duplicates as DuplicateMatch[];
  if (err.errors.some((e) => e.code === "duplicate")) return { kind: "exact", matches };
  if (err.errors.some((e) => e.code === "acknowledge")) return { kind: "probable", matches };
  return null;
}

/**
 * The booking's details pre-filled (UC-APPOINTMENTS-004 step 4, A3): the name as the pull split it, the email and the mobile in
 * 0… form; the patients module's create rules apply (BR-09, BR-06). A probable or exact duplicate lists the matches, each one
 * linkable instead; "Create anyway" re-sends the same patient with the acknowledgement, or with the override and a typed reason
 * for an exact ID match (D42), under the same Idempotency-Key.
 */
function CreateModal({ item, onClose, onDone }: { item: BookingsInboxItem; onClose: () => void; onDone: (what: string) => void }) {
  const name = (item.customerName ?? "").trim();
  const cut = name.lastIndexOf(" ");
  const sp = item.suggestedPatient;
  const [d, setD] = useState({
    firstNames: sp?.firstNames ?? (cut > 0 ? name.slice(0, cut) : name), surname: sp?.surname ?? (cut > 0 ? name.slice(cut + 1) : ""), idNumber: "", dateOfBirth: "", gender: "",
    mobile: sp?.mobile ?? item.customerMobile ?? "", email: sp?.email ?? item.customerEmail ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [dups, setDups] = useState<{ kind: "exact" | "probable"; matches: DuplicateMatch[] } | null>(null);
  const [reason, setReason] = useState("");
  const [key] = useState(() => newIdempotencyKey());
  const set = (k: keyof typeof d) => (e: { target: { value: string } }) => setD((p) => ({ ...p, [k]: e.target.value }));
  const fieldErr = (path: string) => (isApiError(error) ? error.field(path)?.message : undefined);
  const derived = d.idNumber.length === 13 ? parseSaId(d.idNumber) : null;
  const send = async (decision: { duplicateAcknowledged?: true; duplicateOverride?: { ofPatientId: string; reason: string } } = {}) => {
    setError(null);
    const body = {
      firstNames: d.firstNames.trim(), surname: d.surname.trim(),
      ...(d.idNumber.trim() ? { idType: "SaId", idNumber: d.idNumber.trim() } : { idType: "None" }),
      ...(d.dateOfBirth && !derived ? { dateOfBirth: d.dateOfBirth } : {}), ...(d.gender && !derived ? { gender: d.gender } : {}),
      contact: { ...(d.mobile.trim() ? { mobile: d.mobile.trim() } : {}), ...(d.email.trim() ? { email: d.email.trim() } : {}) },
      ...decision,
    };
    const parsed = CreatePatientInputSchema.safeParse(body);
    if (!parsed.success) { setError(new Error(parsed.error.issues.map((i) => i.message).join("; "))); return; }
    setBusy(true);
    try { await bookings.createPatient(item.id, parsed.data, key); onDone("Patient created."); }
    catch (err) {
      const refusal = duplicateRefusal(err);
      if (refusal) setDups(refusal);
      else setError(err);
    }
    finally { setBusy(false); }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    // With the matches on screen, the form's own submit is "Create anyway".
    if (dups?.kind === "exact") { if (reason.trim()) void send({ duplicateOverride: { ofPatientId: dups.matches[0]!.id, reason: reason.trim() } }); return; }
    if (dups?.kind === "probable") { void send({ duplicateAcknowledged: true }); return; }
    void send();
  };
  const link = async (patientId: string) => {
    setBusy(true);
    setError(null);
    try { await bookings.link(item.id, patientId); onDone("Linked."); }
    catch (err) { setError(err); }
    finally { setBusy(false); }
  };
  // An error on a path the form does not show (a refusal of another rule) is shown in the alert, never swallowed.
  const validation = isApiError(error) && error.code === "VALIDATION_FAILED" && error.errors.length > 0 ? error : null;
  const unshown = error !== null && (!validation || validation.errors.some((f) => !CREATE_FIELDS.has(f.path)));
  return (
    <Modal title="Create patient from the booking" meta="the appointment follows at once" wide onClose={onClose} onSubmit={submit} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      {dups
        ? <button className="btn primary" type="submit" disabled={busy || (dups.kind === "exact" && !reason.trim())}>{busy ? "Creating…" : "Create anyway"}</button>
        : <button className="btn primary" type="submit" disabled={busy}>{busy ? "Creating…" : "Create patient and appointment"}</button>}
    </>}>
      {unshown && <Alert tone="crit">{validation ? validation.errors.filter((f) => !CREATE_FIELDS.has(f.path)).map((f) => f.message).join(" ") : describeError(error)}</Alert>}
      {dups && (
        <div className="stack gap-8">
          <Alert tone="warn" role="alert"><b>{dups.kind === "exact" ? "This ID number is already on a patient's record." : "This may be a patient you already have."}</b> Link the booking to them, or create a new patient anyway.</Alert>
          <div className="list bordered">
            {dups.matches.map((m) => (
              <div key={m.id} className="item">
                <div className="grow"><div className="primary">{m.displayName} <span className="mono faint">{m.accountCode}</span></div><div className="sub">born {formatDate(m.dateOfBirth)} · matched on {matchedOnText(m.matchedOn)}</div></div>
                <button className="btn sm" type="button" disabled={busy || m.status === "Archived"} onClick={() => void link(m.id)}>Link to this patient</button>
              </div>
            ))}
          </div>
          {dups.kind === "exact" && <Field label="Why create another patient with this ID number?" required htmlFor="cp-override" help="Stored with the new record and audited (D42)"><TextInput id="cp-override" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} /></Field>}
        </div>
      )}
      <div className="grid-2">
        <Field label="First names" required htmlFor="cp-firstNames" error={fieldErr("firstNames")}><TextInput id="cp-firstNames" required value={d.firstNames} onChange={set("firstNames")} autoFocus /></Field>
        <Field label="Surname" required htmlFor="cp-surname" error={fieldErr("surname")}><TextInput id="cp-surname" required value={d.surname} onChange={set("surname")} /></Field>
        <Field label="SA ID number" htmlFor="cp-idNumber" error={fieldErr("idNumber")} help={derived ? `Date of birth ${derived.dateOfBirth} · ${derived.gender}` : "13 digits; or enter the date of birth below"}><TextInput id="cp-idNumber" value={d.idNumber} inputMode="numeric" maxLength={13} onChange={(e) => setD((p) => ({ ...p, idNumber: e.target.value.replace(/\D/g, "") }))} mono /></Field>
        <Field label="Date of birth" required={!derived} htmlFor="cp-dateOfBirth" error={fieldErr("dateOfBirth")}><TextInput id="cp-dateOfBirth" type="date" value={d.dateOfBirth} disabled={!!derived} onChange={set("dateOfBirth")} mono /></Field>
        <Field label="Gender" required={!derived} htmlFor="cp-gender" error={fieldErr("gender")}><Select id="cp-gender" value={d.gender} disabled={!!derived} onChange={set("gender")}><option value="">—</option><option value="Female">Female</option><option value="Male">Male</option><option value="Other">Other</option></Select></Field>
        <Field label="Mobile" htmlFor="cp-mobile" error={fieldErr("contact.mobile")}><TextInput id="cp-mobile" value={d.mobile} type="tel" onChange={set("mobile")} /></Field>
        <Field label="Email" htmlFor="cp-email" error={fieldErr("contact.email")}><TextInput id="cp-email" value={d.email} type="email" onChange={set("email")} /></Field>
      </div>
      {!dups && <span className="help">A duplicate check runs first: a possible match is listed here to link instead, or to create anyway.</span>}
    </Modal>
  );
}

function IgnoreModal({ item, onClose, onDone }: { item: BookingsInboxItem; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;
    setBusy(true);
    setError(null);
    try { await bookings.ignore(item.id, reason.trim()); onDone(); }
    catch (err) { setError(err); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Ignore ${item.customerName ?? "booking"}`} meta="never imported; later pulls leave it" onClose={onClose} onSubmit={(e) => void submit(e)} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      <button className="btn primary" type="submit" disabled={busy || !reason.trim()}>{busy ? "Ignoring…" : "Ignore booking"}</button>
    </>}>
      {error !== null && <Alert tone="crit">{describeError(error)}</Alert>}
      <Field label="Reason" required htmlFor="inbox-ignore-reason"><TextArea id="inbox-ignore-reason" value={reason} maxLength={500} autoFocus onChange={(e) => setReason(e.target.value)} placeholder="Test booking; cancelled by phone; …" /></Field>
    </Modal>
  );
}
